import { Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';

import { getDebitorSummary, getCreditorSummary } from 'services/tabreport';
import useTranslation from 'customHooks/translate';
import { CREDITOR_BALANCES, DEBITOR_BALANCES } from 'events/types';

import BalanceInCurrency from './BalanceCurrency';

interface CurrencyData {
  currencyId?: string;
  currencyid?: string;
  total: number;
  [key: string]: any;
}

const TransactionBalance = () => {
  const defaultDebtType = 0;
  const [debtType, setDebtType] = useState<number>(defaultDebtType);
  const [showTotals, setShowTotals] = useState<boolean>(false);

  const translation = useTranslation();

  const {
    data: debitorData,
    refetch: refetchDebitor,
    isLoading: isDebitorLoading,
  } = useQuery<CurrencyData[]>({
    queryKey: [DEBITOR_BALANCES],
    queryFn: async () => {
      const response = await getDebitorSummary();
      return response?.data || [];
    },
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: 'always',
    retry: 1,
  });

  const {
    data: creditorData,
    refetch: refetchCreditor,
    isLoading: isCreditorLoading,
  } = useQuery<CurrencyData[]>({
    queryKey: [CREDITOR_BALANCES],
    queryFn: async () => {
      const response = await getCreditorSummary();
      return response?.data || [];
    },
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: 'always',
    retry: 1,
  });

  const isLoading = isDebitorLoading || isCreditorLoading;

  const balanceData = useMemo(() => {
    if (!debitorData && !creditorData) return null;

    const convertArrayToObject = (data: CurrencyData[]) => {
      const result: { [key: string]: number } = {};
      if (Array.isArray(data)) {
        data.forEach((item) => {
          if (item) {
            const currencyCode = item.currencyId || item.currencyid;
            if (currencyCode) {
              const key = `${currencyCode.toLowerCase()}Total`;
              result[key] = item.total || 0;
            }
          }
        });
      }
      return result;
    };

    const debitorObj = debitorData ? convertArrayToObject(debitorData) : {};
    const creditorObj = creditorData ? convertArrayToObject(creditorData) : {};

    return [debitorObj, creditorObj];
  }, [debitorData, creditorData]);

  const displayData = useMemo(() => {
    if (debtType === 0 && debitorData) {
      return debitorData
        .map((item) => {
          const currencyCode = item?.currencyId || item?.currencyid;
          if (currencyCode) {
            return {
              [`${currencyCode.toLowerCase()}Total`]: item.total || 0,
            };
          }
          return {};
        })
        .filter((item) => Object.keys(item).length > 0);
    } else if (debtType === 1 && creditorData) {
      return creditorData
        .map((item) => {
          const currencyCode = item?.currencyId || item?.currencyid;
          if (currencyCode) {
            return {
              [`${currencyCode.toLowerCase()}Total`]: item.total || 0,
            };
          }
          return {};
        })
        .filter((item) => Object.keys(item).length > 0);
    } else if (debtType === 2 && balanceData) {
      return balanceData;
    }
    return null;
  }, [debtType, debitorData, creditorData, balanceData]);

  useEffect(() => {
    refetchDebitor();
    refetchCreditor();
  }, [refetchDebitor, refetchCreditor]);

  // Handle select change with data refetch
  const handleSelectChange = async (value: unknown) => {
    const selectedDebtType = parseInt(value as string, 10);
    setDebtType(selectedDebtType);
    setShowTotals(selectedDebtType === 2);

    // Refetch both APIs when selection changes
    await Promise.all([refetchDebitor(), refetchCreditor()]);
  };

  const isDataReady = !isLoading && displayData;

  return (
    <div className="flex flex-col gap-[12px] p-[16px] md:border-l md:border-l-[#f5f7f9]">
      <div className="flex h-[48px] items-center rounded-[6px] border border-[rgba(70,106,251,0.12)]">
        <SelectStyled
          defaultValue={String(defaultDebtType)}
          className="w-full text-[14px] font-medium text-[#1D1D1D]"
          bordered={false}
          onSelect={handleSelectChange}
        >
          <Select.Option value="0">
            {translation.dashboard.SideReport_CBItemsDr}
          </Select.Option>
          <Select.Option value="1">
            {translation.dashboard.SideReport_CBItemsCr}
          </Select.Option>
          <Select.Option value="2">
            {translation.dashboard.SideReport_CBItemsBoth}
          </Select.Option>
        </SelectStyled>
      </div>

      {isLoading && (
        <div className="flex h-20 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#466AFB]"></div>
        </div>
      )}

      {isDataReady && (
        <BalanceInCurrency data={displayData} showTotals={showTotals} />
      )}

      {!isLoading && !displayData && (
        <div className="flex h-20 items-center justify-center text-gray-500">
          <div>No currency data available</div>
        </div>
      )}
    </div>
  );
};

const SelectStyled = styled(Select)`
  & .ant-select-selector {
    width: 100% !important;
    border: none !important;
    box-shadow: none !important;
  }

  & .ant-select-selection-item {
    font-weight: 400;
    color: #466afb;
  }

  &.ant-select-focused .ant-select-selector {
    border: none !important;
    box-shadow: none !important;
  }
`;

export default TransactionBalance;
